import { themeAlpine } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import classNames from 'classnames'
import { forwardRef, memo, useCallback, useEffect, useState } from 'react'
import { MdAdd } from 'react-icons/md'

import type { FilterChangedEvent, FirstDataRenderedEvent, PaginationChangedEvent } from 'ag-grid-community'
import type { AgGridReactProps } from 'ag-grid-react'
import type { RefObject, ChangeEvent, ReactElement, ReactNode } from 'react'

import { commonProps, sideBar } from '../common'

import Button from '~/components/Buttons/Button'
import SearchInput from '~/components/Inputs/Basic/SearchInput'
import Pagination from '~/components/Pagination'
import ShowItems from '~/components/ShowItems'
import { getButtonWithTooltip } from '~/utils/table'

import './styles.scss'

const DEFAULT_ENTRIES_PER_PAGE = 10
const DEFAULT_ENTRIES_PER_PAGE_WITHOUT_PAGINATION = 1000

const theme = themeAlpine.withParams({ accentColor: '#ff5301' })

export type IAddButtonTooltip = 'default' | 'unauthorized'

const getTotalPage = (total: number, elementPerPage: number): number =>
  total === 0 ? 0 : Math.ceil(total >= elementPerPage ? total / elementPerPage : 1)

export interface Props extends AgGridReactProps {
  addButton?: boolean
  addButtonDisabled?: boolean
  addButtonLabel?: string
  addButtonTooltip?: IAddButtonTooltip
  autoSizeColumnToFit?: boolean
  buttonComponent?: ReactElement
  className?: string
  editControls?: ReactNode
  entriesName: string
  isSideBar?: boolean
  nodeBeforeSearchBar?: ReactElement
  onAddButtonClick?: VoidFunction
  onSearchTextChange?: (value: string) => void
  searchPlaceholder?: string
  tableRef?: RefObject<HTMLDivElement>
  withPagination?: boolean
  withSearchBar?: boolean
  withSearchBarAutoFocus?: boolean
}

const Table = forwardRef<AgGridReact, Props>(
  (
    {
      addButton,
      addButtonDisabled,
      addButtonLabel,
      addButtonTooltip,
      autoSizeColumnToFit = true,
      buttonComponent,
      className,
      columnDefs,
      editControls,
      entriesName,
      isSideBar = false,
      nodeBeforeSearchBar,
      onAddButtonClick,
      onFilterChanged,
      onSearchTextChange,
      rowData,
      searchPlaceholder,
      tableRef,
      withPagination = true,
      withSearchBar,
      withSearchBarAutoFocus,
      ...props
    },
    ref
  ): ReactElement => {
    /** Local state */

    const storedItemPerPage = localStorage.getItem(entriesName)

    const [currentPage, setCurrentPage] = useState(0)

    const [elementPerPage, setElementPerPage] = useState(DEFAULT_ENTRIES_PER_PAGE)

    const [totalNumberElements, setTotalNumberElements] = useState(0)

    const [quickFilter, setQuickFilter] = useState<string>()

    const tableContainerClasses = classNames('table-component', className, {
      'hidden-pagination': getTotalPage(totalNumberElements, elementPerPage) <= 1 || !withPagination,
    })

    const searchContainerClasses = classNames('search-container', { 'with-searchbar': withSearchBar })

    /** Effects */

    const sizeColumnsToFit = useCallback(() => {
      // @ts-expect-error current not defined
      autoSizeColumnToFit && ref?.current.api.sizeColumnsToFit()
    }, [ref, autoSizeColumnToFit])

    useEffect(() => {
      window.addEventListener('resize', sizeColumnsToFit)
      return () => {
        window.removeEventListener('resize', sizeColumnsToFit)
      }
    }, [sizeColumnsToFit])

    //////// PAGINATION METHODS ////////

    useEffect(() => {
      if (rowData && rowData.length > 0) {
        setTotalNumberElements(rowData.length)
      }
    }, [elementPerPage, rowData])

    useEffect(() => {
      if (ref) {
        let itemsPerPage = 0
        const defaultElmsPerPage = storedItemPerPage ? +storedItemPerPage : DEFAULT_ENTRIES_PER_PAGE

        if (!withPagination) {
          itemsPerPage = DEFAULT_ENTRIES_PER_PAGE_WITHOUT_PAGINATION
        } else if (!rowData?.length) {
          itemsPerPage = DEFAULT_ENTRIES_PER_PAGE
        } else if (defaultElmsPerPage <= rowData.length) {
          itemsPerPage = defaultElmsPerPage
        } else {
          itemsPerPage = (Math.floor(rowData.length / 10) + 1) * 10
        }

        setElementPerPage(itemsPerPage)
      }
    }, [ref, rowData?.length, storedItemPerPage, withPagination])

    /** Handlers */

    const handleFirstDataRendered = useCallback((evt: FirstDataRenderedEvent) => {
      evt.api.sizeColumnsToFit()
    }, [])

    const handlePaginationChanged = useCallback((evt: PaginationChangedEvent) => {
      setCurrentPage(evt.api.paginationGetCurrentPage())
    }, [])

    const handleSearchTextChange = useCallback(
      (evt: ChangeEvent<HTMLInputElement>) => {
        setQuickFilter(evt.target.value)
        onSearchTextChange && onSearchTextChange(evt.target.value)
      },
      [onSearchTextChange]
    )

    const handleFilterChanged = (evt: FilterChangedEvent) => {
      onFilterChanged && onFilterChanged(evt)
      const rowCount = evt.api.getDisplayedRowCount()
      setTotalNumberElements(rowCount)
    }

    const goBack = useCallback(() => {
      // @ts-expect-error current not defined
      ref?.current.api.paginationGoToPreviousPage()
    }, [ref])

    const goNext = useCallback(() => {
      // @ts-expect-error current not defined
      ref?.current.api.paginationGoToNextPage()
    }, [ref])

    const goToPage = useCallback(
      (page: number) => {
        // @ts-expect-error current not defined
        ref?.current.api.paginationGoToPage(page)
      },
      [ref]
    )

    const handleItemPerPageChange = useCallback((itemsPerPage: number) => {
      setElementPerPage(itemsPerPage)
    }, [])

    return (
      <div className={tableContainerClasses}>
        {withSearchBar || addButton || withPagination ? (
          <div className={searchContainerClasses}>
            {nodeBeforeSearchBar}
            {withSearchBar ? (
              <div className="table-search-bar-container">
                <SearchInput
                  autoFocus={withSearchBarAutoFocus}
                  className="table-search-bar"
                  onChange={handleSearchTextChange}
                  placeholder={searchPlaceholder}
                />
              </div>
            ) : null}
            {buttonComponent}
            {addButton ? (
              addButtonTooltip ? (
                getButtonWithTooltip(
                  addButtonTooltip,
                  <Button disabled={addButtonDisabled} onClick={onAddButtonClick}>
                    <MdAdd color="#fff" size={16} /> {addButtonLabel}
                  </Button>
                )
              ) : (
                <Button disabled={addButtonDisabled} onClick={onAddButtonClick}>
                  <MdAdd color="#fff" size={16} /> {addButtonLabel}
                </Button>
              )
            ) : null}
            {editControls ? <div className="edition-controls-wrapper">{editControls}</div> : null}
            {rowData && rowData.length > 0 && withPagination ? (
              <ShowItems
                className="table-show-items-component"
                entriesName={entriesName}
                numberPerPage={elementPerPage}
                setNumberPerPage={handleItemPerPageChange}
                totalNumberElements={totalNumberElements}
              />
            ) : null}
          </div>
        ) : null}
        <div className="table-wrapper ag-theme-alpine" ref={tableRef ?? undefined}>
          <AgGridReact
            {...commonProps}
            columnDefs={columnDefs}
            onFilterChanged={handleFilterChanged}
            onFirstDataRendered={handleFirstDataRendered}
            onPaginationChanged={handlePaginationChanged}
            pagination={withPagination}
            paginationPageSize={elementPerPage}
            quickFilterText={quickFilter}
            ref={ref}
            rowData={rowData}
            sideBar={isSideBar ? sideBar : undefined}
            theme={theme}
            {...props}
          />
        </div>
        {withPagination && totalNumberElements !== 0 ? (
          <Pagination
            className="table-pagination"
            currentPage={currentPage}
            goBack={goBack}
            goNext={goNext}
            goToPage={goToPage}
            pageTotal={getTotalPage(totalNumberElements, elementPerPage)}
          />
        ) : null}
      </div>
    )
  }
)

Table.displayName = 'Table'

export default memo(Table)
